/**
 * @tt/players — the single source of truth for player identity across the
 * whole platform: the player cap, the slot type, the canonical slot color
 * palette, and count-derived layout helpers.
 *
 * WHY THIS PACKAGE EXISTS (8-player program, 2026-07-03): the 4-player cap
 * was never one decision — it was the number 4 scattered across ~81 slot
 * union types, ~126 `% 4` sites, and 20+ copy-pasted SLOT_COLORS arrays in
 * TWO divergent versions. Centralizing here is what makes the NEXT cap
 * change ("go to 12") a ≤3-file edit instead of a re-audit.
 *
 * RULES (enforced by tools/check-scattered-slots.mjs):
 *  - No game/app file may declare its own slot palette. Import from here
 *    (directly or via @tt/shell-3d's re-export).
 *  - No bare `% 4` against a slot. Use {@link slotColor} — it wraps safely.
 *  - The palette lives in palette.json so the CVD validator, visual-QA
 *    tooling, and this module can never drift apart.
 *
 * Dependency-free (no DOM, no three, no React) — safe for game servers,
 * lambdas, web, tools, and any client bundle.
 */
import palette from './palette.json';

/**
 * A player's seat index within a lobby, 0-based.
 *
 * Deliberately `number`, not a literal union: the 0|1|2|3 union bought no
 * real safety (payloads arrive as `number` off the wire anyway and were
 * cast at ~81 sites) and made every cap change a type-system sweep.
 * Validate at trust boundaries with {@link isValidSlot} instead.
 */
export type Slot = number;

/**
 * Hard platform cap on seated (non-spectator) players per lobby.
 *
 * FLIPPED to 8 on 2026-07-04 (8P program): lobby, presence netcode, UI
 * surfaces, scoring, and 19 game servers are certified count-driven.
 * Games opt in via their own manifest maxPlayers; lane/team games stay
 * ≤4. Do not gate player counts on any other number anywhere.
 */
export const MAX_PLAYERS = 8;

/** The design ceiling the platform is built to: palette size, layout
 *  helpers, and QA harnesses are validated to this count. MAX_PLAYERS may
 *  never exceed it without extending the palette first. */
export const MAX_SUPPORTED_PLAYERS = 8;

/** True iff `s` is a valid seat index under the given cap (default: the
 *  live MAX_PLAYERS). Use at trust boundaries (wire payloads, DDB reads). */
export function isValidSlot(s: unknown, max: number = MAX_PLAYERS): s is Slot {
  return typeof s === 'number' && Number.isInteger(s) && s >= 0 && s < max;
}

// ── Canonical slot palette ──────────────────────────────────────────────
// Slots 1-4 = the shipped World-Art-Bible identity palette (P1 Ember,
// P2 Teal, P3 Violet, P4 Gold). Slots 5-8 added for the 8P program,
// CVD-validated by tools/validate-palette.mjs against the shipped
// baseline. Values live in palette.json — edit THERE only.

interface PaletteEntry { name: string; hex: string }
const ENTRIES: PaletteEntry[] = (palette as { slots: PaletteEntry[] }).slots;

/** Canonical slot colors as 0xRRGGBB numbers, indexed by slot. Length is
 *  MAX_SUPPORTED_PLAYERS. Prefer {@link slotColor} over direct indexing —
 *  it wraps out-of-range slots instead of returning undefined. */
export const SLOT_COLORS: readonly number[] = ENTRIES.map((e) => Number.parseInt(e.hex, 16));

/** Human names for the slot colors (QA sheets, docs, cosmetic labels). */
export const SLOT_COLOR_NAMES: readonly string[] = ENTRIES.map((e) => e.name);

/** The slot's identity color as a 0xRRGGBB number. Wraps out-of-range
 *  slots (defensive: a bad slot renders as SOME valid color rather than
 *  crashing a render loop mid-match). */
export function slotColor(slot: Slot): number {
  const n = SLOT_COLORS.length;
  const i = ((Math.trunc(slot) % n) + n) % n;
  return SLOT_COLORS[i]!;
}

/** The slot's identity color as a `#rrggbb` CSS string. */
export function slotColorCss(slot: Slot): string {
  return `#${slotColor(slot).toString(16).padStart(6, '0')}`;
}

/** HSL hue (0-360) of the slot's identity color — for games that derive
 *  tinted materials/inks from the slot hue (e.g. one-of-us ink). */
export function slotHue(slot: Slot): number {
  const c = slotColor(slot);
  const r = ((c >> 16) & 0xff) / 255;
  const g = ((c >> 8) & 0xff) / 255;
  const b = (c & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h < 0 ? h + 360 : h;
}

// ── Count-derived layout helpers ────────────────────────────────────────
// Any surface that positions "one thing per player" derives geometry from
// the live player count via these — never from a hardcoded 4-entry table.

/** `n` positions evenly spaced on a circle of `radius` in the XZ plane,
 *  starting at `startAngle` (radians, default: facing the camera at -Z
 *  side, i.e. -PI/2) and proceeding clockwise when viewed from above.
 *  Returns `{x, z, angle}` per seat — `angle` is the outward-facing yaw. */
export function ringPositions(
  n: number,
  radius: number,
  startAngle: number = -Math.PI / 2,
): Array<{ x: number; z: number; angle: number }> {
  if (n <= 0) return [];
  const out: Array<{ x: number; z: number; angle: number }> = [];
  for (let i = 0; i < n; i++) {
    const a = startAngle + (i / n) * Math.PI * 2;
    out.push({ x: Math.cos(a) * radius, z: Math.sin(a) * radius, angle: a });
  }
  return out;
}

/** `n` positions centered on 0, spaced `gap` apart along one axis —
 *  lineups, podium rows, seat rows. (The lobby hall's seatPositions.) */
export function rowPositions(n: number, gap: number): number[] {
  if (n <= 0) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push((i - (n - 1) / 2) * gap);
  return out;
}

/** Split `n` positions into up to `perRow` per row (grids of players:
 *  8-seat intro lineups render as 2 rows of 4). Returns row-major
 *  `{x, y}` offsets in units of `gapX`/`gapY`, centered on 0. */
export function gridPositions(
  n: number,
  perRow: number,
  gapX: number,
  gapY: number,
): Array<{ x: number; y: number; row: number; col: number }> {
  if (n <= 0 || perRow <= 0) return [];
  const rows = Math.ceil(n / perRow);
  const out: Array<{ x: number; y: number; row: number; col: number }> = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / perRow);
    const inRow = row === rows - 1 ? n - row * perRow : perRow;
    const col = i % perRow;
    out.push({
      x: (col - (inRow - 1) / 2) * gapX,
      y: (row - (rows - 1) / 2) * gapY,
      row,
      col,
    });
  }
  return out;
}
